// src/components/WalletPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuLayout from "./MenuLayout";
import "../styles/WalletPage.css";
import { createAddress, getAddresses, getTransactions, getWallet, verifyAddress } from "../api/wallet";
import { createCartItem } from "../api/cart";

const WalletPage = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [error, setError] = useState("");
  const [subtitle] = useState("افزودن و مدیریت آدرس‌ها و افزایش موجودی کیف پول.");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [newAddress, setNewAddress] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [addressId, setAddressId] = useState(null);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [topUpMessage, setTopUpMessage] = useState("");
  const [topUpLoading, setTopUpLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [w, tx, addrs] = await Promise.all([getWallet(), getTransactions(), getAddresses()]);
        setWallet(w);
        setTransactions(tx || []);
        setAddresses(addrs || []);
      } catch (err) {
        setError(err.message || "خطا در دریافت اطلاعات کیف پول");
      }
    };
    load();
  }, []);

  const handleAddAddress = async () => {
    if (!newAddress) return;
    try {
      const res = await createAddress(newAddress);
      setAddressId(res?.address_id || null);
      setShowAddModal(false);
      setNewAddress("");
      const addrs = await getAddresses();
      setAddresses(addrs || []);
    } catch (err) {
      setError(err.message || "خطا در ثبت آدرس");
    }
  };

  const handleVerifyAddress = async () => {
    if (!addressId || !verifyCode) return;
    try {
      await verifyAddress(addressId, verifyCode);
      setVerifyCode("");
      setAddressId(null);
      const addrs = await getAddresses();
      setAddresses(addrs || []);
    } catch (err) {
      setError(err.message || "کد تایید نادرست است");
    }
  };

  const handleTopUp = async () => {
    if (!topUpAmount) return;
    setTopUpLoading(true);
    setTopUpMessage("");
    try {
      await createCartItem({
        item_type: "wallet",
        name: "wallet",
        market_type: "forex",
        investment_amount: topUpAmount,
        price: topUpAmount,
        platform_type: "MT4",
        broker_type: "1",
      });
      setTopUpMessage("درخواست افزایش موجودی ثبت شد.");
      setShowTopUpModal(false);
      navigate("/cart");
    } catch (err) {
      setTopUpMessage(err?.message || "خطا در ثبت افزایش موجودی");
    } finally {
      setTopUpLoading(false);
    }
  };

  const cardNumber = wallet?.wallet_number || "**** **** **** ****";
  const balanceLabel = wallet ? `${wallet.balance} USD` : "—";

  return (
    <MenuLayout>
      <div className="wallet-page">
        <div className="wallet-top">
          <div className="wallet-info">
            <header className="wallet-header">
              <h1 className="wallet-title">کیف پول</h1>
              <p className="wallet-subtitle">{subtitle}</p>
              <div className="wallet-balance">
                <span className="wallet-balance-label">موجودی :</span>
                <span className="wallet-balance-value">{balanceLabel}</span>
              </div>
            </header>

            <div className="wallet-actions">
              <div className="wallet-tabs">
                <button className="wallet-tab wallet-tab-active">
                  <img src="/images/wallet/deposit.svg" alt="" />
                  واریز
                </button>
                <span className="wallet-tab-sep" />
                <button className="wallet-tab">
                  <img src="/images/wallet/withdraw.svg" alt="" />
                  برداشت
                </button>
              </div>

              <button className="wallet-add-card" onClick={() => setShowAddModal(true)}>
                <span className="wallet-add-icon">+</span>
                <span>افزودن آدرس جدید</span>
              </button>

              <button className="wallet-topup-btn" onClick={() => setShowTopUpModal(true)}>
                <span className="wallet-topup-icon">⟳</span>
                <span>شارژ کیف پول</span>
              </button>
            </div>
          </div>

          <div className="wallet-card-wrapper">
            <div className="wallet-card">
              <div className="wallet-card-logo">
                <img src="/images/wallet/card-logo.svg" alt="Touch Prop" />
              </div>
              <div className="wallet-card-balance">
                <div className="wallet-card-balance-label">موجودی</div>
                <div className="wallet-card-balance-value">{balanceLabel}</div>
              </div>
              <div className="wallet-card-number">{cardNumber}</div>
            </div>
          </div>
        </div>

        {addresses.length > 0 && (
          <div className="wallet-addresses">
            <h3 className="wallet-address-title">آدرس‌های شما</h3>
            <div className="wallet-address-list">
              {addresses.map((addr) => (
                <div className="wallet-address-item" key={addr.id}>
                  <div className="wallet-address-text">{addr.address}</div>
                  <div className="wallet-address-status">
                    {addr.is_verified ? "تایید شده" : "در انتظار تایید"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="wallet-table">
          <div className="wallet-table-head">
            <span>#</span>
            <span>تراکنش</span>
            <span>مبلغ</span>
            <span>تاریخ</span>
            <span>وضعیت</span>
            <span>آدرس</span>
            <span></span>
          </div>
          <div className="wallet-table-body">
            {transactions.map((tx, idx) => (
              <div className="wallet-table-row" key={tx.id || idx}>
                <span>{idx + 1}</span>
                <span>{tx.transaction_type_display || tx.transaction_type}</span>
                <span className="wallet-amount">{tx.amount}</span>
                <span>{tx.created_at}</span>
                <span className="wallet-status">{tx.status_display || tx.status}</span>
                <span className="wallet-cardnum">{tx.address || cardNumber}</span>
                <span className="wallet-more">...</span>
              </div>
            ))}
          </div>
        </div>

        {showAddModal && (
          <div className="wallet-modal-backdrop" onClick={() => setShowAddModal(false)}>
            <div className="wallet-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="wallet-modal-title">اضافه کردن آدرس جدید</h3>
              <label className="wallet-modal-field">
                <span>آدرس کیف پول (bep20):</span>
                <input type="text" value={newAddress} onChange={(e) => setNewAddress(e.target.value)} placeholder="0x..." />
              </label>
              <div className="wallet-modal-actions">
                <button className="wallet-modal-btn wallet-modal-cancel" onClick={() => setShowAddModal(false)}>
                  انصراف
                </button>
                <button className="wallet-modal-btn wallet-modal-submit" onClick={handleAddAddress}>
                  ثبت آدرس
                </button>
              </div>
            </div>
          </div>
        )}

        {addressId && (
          <div className="wallet-modal-backdrop" onClick={() => setAddressId(null)}>
            <div className="wallet-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="wallet-modal-title">تایید آدرس</h3>
              <p className="wallet-modal-desc">کد تایید ارسال شده به ایمیل را وارد کنید.</p>
              <label className="wallet-modal-field">
                <span>کد تایید:</span>
                <input type="text" value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)} placeholder="123456" />
              </label>
              <div className="wallet-modal-actions">
                <button className="wallet-modal-btn wallet-modal-cancel" onClick={() => setAddressId(null)}>
                  انصراف
                </button>
                <button className="wallet-modal-btn wallet-modal-submit" onClick={handleVerifyAddress}>
                  تایید
                </button>
              </div>
            </div>
          </div>
        )}

        {showTopUpModal && (
          <div className="wallet-modal-backdrop" onClick={() => setShowTopUpModal(false)}>
            <div className="wallet-modal wallet-topup-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="wallet-modal-title wallet-topup-title">افزایش موجودی</h3>
              <label className="wallet-modal-field wallet-topup-field">
                <span>مبلغ افزایش موجودی :</span>
                <div className="wallet-topup-input">
                  <span className="wallet-topup-unit">$</span>
                  <input type="number" value={topUpAmount} onChange={(e) => setTopUpAmount(e.target.value)} />
                </div>
              </label>
              {topUpMessage && <div className="wallet-topup-message">{topUpMessage}</div>}
              <button className="wallet-topup-submit" onClick={handleTopUp} disabled={topUpLoading}>
                {topUpLoading ? "در حال ثبت..." : "افزایش موجودی"}
              </button>
            </div>
          </div>
        )}

        {error && <div className="wallet-error">{error}</div>}
      </div>
    </MenuLayout>
  );
};

export default WalletPage;
